export type WorkoutView = 'today' | 'history' | 'templates';

export interface User {
  id: string;
  username: string;
  email: string;
  preferredLocale?: string;
}

// Unit axis for a workout set's numeric value, mirrors MOVEMENT_UNITS in
// gym-planner-backend/src/types.ts. No DB CHECK - workout_sets.unit / template_exercises.unit is
// unconstrained text, so this union is compile-time only (same convention as EXERCISE_TYPES).
//
// 'sec' (TODO.md E, item 5 - decided 2026-08-20): stretching/mobility exercises collect a hold-time
// value in seconds, stored in the existing `reps` field with unit 'sec' rather than a new `duration`
// field; `weight` is 0/hidden for a 'sec' set. Any UI rendering a set's reps/weight/unit as text must
// branch on `unit === 'sec'` and show "Xs hold" instead of "X reps @ Y kg".
export const MOVEMENT_UNITS = ['kg', 'lb', 'bw', 'assist', 'sec'] as const;
export type MovementUnit = typeof MOVEMENT_UNITS[number];

export interface Exercise {
  id: string;
  name: string;
  sets: number;
  reps: number;
  weight: number;
  unit: MovementUnit;
  completed: boolean;
}

export interface WorkoutHistory {
  id: string;
  userId: string;
  date: Date;
  exerciseCount: number;
  duration: string;
  exercises: Array<{
    id: string; // Exercise ID for 1-to-1 mapping
    name: string; // Keep name for display and backward compatibility
    completed: boolean;
    sets: Array<{
      reps: number;
      weight: number;
      unit: MovementUnit;
    }>;
  }>;
}

export type MuscleGroup =
  | 'all'
  | 'chest'
  | 'back'
  | 'shoulders'
  | 'biceps'
  | 'triceps'
  | 'arms'
  | 'legs'
  | 'abs'
  | 'forearms';

// Exercise type axis (movement kind), mirrors EXERCISE_TYPES in gym-planner-backend/src/types.ts.
// Keep in sync with the backend CHECK constraint (database/migration/7.sql) -- see
// docs/EXERCISE-TYPES-GUIDE.md for the full model (muscleGroup / exerciseType / categories axes).
export const EXERCISE_TYPES = ['strength', 'stretching', 'mobility'] as const;
export type ExerciseType = typeof EXERCISE_TYPES[number];

// Movement pattern axis (B5), mirrors MOVEMENT_PATTERNS in gym-planner-backend/src/types.ts. Keep in
// sync with the backend CHECK constraint (database/migration/16.sql). Nullable/optional by design -
// several exercises don't classify as either (see the migration's header); `undefined` means "not
// classified or not applicable", never a third displayed value beyond compound/isolation/unset.
export const MOVEMENT_PATTERNS = ['compound', 'isolation'] as const;
export type MovementPattern = typeof MOVEMENT_PATTERNS[number];

// Cap on exercises.notes (ADR-006 / TODO.md B4), mirrors EXERCISE_NOTES_MAX_LENGTH in
// gym-planner-backend/src/types.ts. Enforced server-side; this is a client-side UX affordance (live
// character count) only, matching the pattern FeedbackPage.tsx already uses for its own capped field.
export const EXERCISE_NOTES_MAX_LENGTH = 4000;

// Exercise definition (for the exercise library)
export interface ExerciseDefinition {
  id: string;
  name: string;
  localizedName?: string;
  muscleGroup: MuscleGroup;
  exerciseType?: ExerciseType;
  movementPattern?: MovementPattern;
  categories?: string[];
  imageUrl?: string;
  externalUrl?: string; // External URL for exercise demo (e.g., GIF, video)
  description?: string;
  localizedDescription?: string;
  /** How to do it well - execution cues, form, safety (TODO.md B4). Detail view only - never present on
   * list/search responses (AC-EX-20), so only read this on a single-exercise fetch (GET /exercises/:id). */
  notes?: string;
  localizedNotes?: string;
  equipment?: string;
  references?: string[]; // Instagram Reel URLs or other video references
  userId?: string; // Owner of the exercise
  isPrivate?: boolean; // Private exercises only visible to owner
}

// Exercise in a workout (with sets, reps, etc.)
export interface WorkoutExercise extends ExerciseDefinition {
  sets: number;
  completedSets: number;
  reps: number;
  weight: number;
  unit: any;
  completed: boolean;
  inProgress?: boolean;
  setsData?: Array<{ reps: number; weight: number; unit: MovementUnit }>; // Optional detailed set data
}

// Workout template data (as stored in the backend)
export interface WorkoutTemplateData {
  id: string;
  name: string;
  localizedName?: string;
  description?: string;
  localizedDescription?: string;
  muscleGroups: MuscleGroup[];
  exercises: Array<{
    exerciseId: string;
    sets: number;
    reps: number;
    weight: number;
    unit: MovementUnit;
  }>;
  createdAt?: string;
  updatedAt?: string;
  userId?: string;
  isPrivate?: boolean;
}

// Workout template with exercises hydrated
export interface WorkoutTemplate {
  id: string;
  name: string;
  localizedName?: string;
  description?: string;
  localizedDescription?: string;
  muscleGroups: MuscleGroup[]; // Target muscle groups for this template
  exercises: WorkoutExercise[];
  createdAt?: string;
  updatedAt?: string;
  userId?: string; // Owner of the template
  isPrivate?: boolean; // Private templates only visible to owner
}
